/**
 * Utilitaire pour la gestion des positions de basketball
 * Fournit un mapping cohérent entre les clés de base de données et les traductions
 */

// Clés des positions (utilisées en base de données et comme référence)
export const POSITION_KEYS = [
  'pointGuard',
  'shootingGuard',
  'smallForward',
  'powerForward',
  'center'
];

// Mapping des clés de DB vers les clés de traduction
// Les positions en DB sont stockées dans la langue du club
// Ce mapping permet de convertir les anciennes valeurs espagnoles
export const DB_TO_TRANSLATION_KEY = {
  // Espagnol (legacy)
  'Base': 'pointGuard',
  'Escolta': 'shootingGuard',
  'Alero': 'smallForward',
  'Ala-Pivot': 'powerForward',
  'Ala-Pívot': 'powerForward',
  'Pivot': 'center',
  'Pívot': 'center',
  // Français
  'Meneur': 'pointGuard',
  'Arrière': 'shootingGuard',
  'Ailier': 'smallForward',
  'Ailier fort': 'powerForward',
  // Anglais
  'Point Guard': 'pointGuard',
  'Shooting Guard': 'shootingGuard',
  'Small Forward': 'smallForward',
  'Power Forward': 'powerForward',
  'Center': 'center'
};

/**
 * Hook personnalisé pour obtenir les positions traduites
 * @param {Function} t - Fonction de traduction de i18next
 * @returns {Object} - Objet contenant les positions et helpers
 */
export const usePositions = (t) => {
  // Liste des positions traduites (pour les dropdowns, etc.)
  const positions = t('positions.list', { returnObjects: true }) || [];

  // Obtenir une position traduite par sa clé
  const getPosition = (key) => t(`positions.${key}`);

  // Obtenir toutes les positions comme objet { key: label }
  const positionsMap = POSITION_KEYS.reduce((acc, key) => {
    acc[key] = t(`positions.${key}`);
    return acc;
  }, {});

  // Obtenir les positions avec clé et label pour les selects
  // Retourne [{ key: 'pointGuard', label: 'Base' }, ...]
  const positionsWithKeys = POSITION_KEYS.map(key => ({
    key,
    label: t(`positions.${key}`)
  }));

  // Convertir une valeur de DB vers la clé de traduction
  const getTranslationKeyFromDB = (dbValue) => {
    // Si c'est déjà une clé valide, la retourner
    if (POSITION_KEYS.includes(dbValue)) {
      return dbValue;
    }
    return DB_TO_TRANSLATION_KEY[dbValue] || null;
  };

  // Traduire une position depuis la DB vers la langue actuelle 
  const translateFromDB = (dbValue) => {
    // Si c'est déjà une clé valide, traduire directement
    if (POSITION_KEYS.includes(dbValue)) {
      return t(`positions.${dbValue}`);
    }
    const key = getTranslationKeyFromDB(dbValue);
    return key ? t(`positions.${key}`) : dbValue;
  };

  return {
    positions,        // Array des positions traduites ['Base', 'Escolta', ...]
    positionsMap,     // Object { pointGuard: 'Base', ... }
    positionsWithKeys, // Array [{ key: 'pointGuard', label: 'Base' }, ...]
    getPosition,      // Function (key) => traduction
    translateFromDB,  // Function (dbValue) => traduction dans langue courante
    POSITION_KEYS     // Array des clés ['pointGuard', 'shootingGuard', ...]
  };
};

/**
 * Obtenir les positions sans hook (pour utilisation hors composants)
 * @param {Function} t - Fonction de traduction
 * @returns {Array} - Liste des positions traduites
 */
export const getPositionsList = (t) => {
  return t('positions.list', { returnObjects: true }) || [];
};

export default usePositions;
